import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, retry, shareReplay, throwError, timer } from 'rxjs';
import type { CheckinPayload } from 'src/app/api/generated/v3/schemas/checkinPayload';
import { CheckinPayloadTipo } from 'src/app/api/generated/v3/schemas/checkinPayloadTipo';
import type { CheckinResultadoDto } from 'src/app/api/generated/v3/schemas/checkinResultadoDto';
import type { CheckinSessionResponse } from 'src/app/api/generated/v3/schemas/checkinSessionResponse';

/**
 * Escrita de check-in pela v3 (GUE-392 / GUE-359).
 *
 * Três coisas que NÃO se confundem, mesmo passando todas pelo mesmo botão:
 *
 *  - **habilitação** — a tela estar ligada é o flag `checkinsV2`, resolvido no
 *    `checkinGuard`. Nada aqui olha flag.
 *  - **autorização** — quem pode registrar check-in é CASL no cliente
 *    (`update Check-ins`, via `checkin-row.vm.ts`) e, no servidor, a permissão
 *    no banco que o `CheckInControllerV3` exige. Este arquivo não decide
 *    permissão nenhuma.
 *  - **transporte** — é o que mora aqui: mandar a escrita com
 *    `Idempotency-Key` e não duplicar a operação quando algo dá errado.
 */

/** Renova quando faltam cinco minutos, como manda o contrato da sessão. */
const MARGEM_RENOVACAO_MS = 5 * 60 * 1000;

/** Falhas em que repetir a MESMA operação é seguro (a chave é a mesma). */
const TRANSITORIOS = new Set([0, 408, 502, 503, 504]);

function ehTransitorio(err: unknown): boolean {
  return err instanceof HttpErrorResponse && TRANSITORIOS.has(err.status);
}

interface Sessao {
  eventoId: string;
  token: string;
  /** Instante local de expiração — ver comentário em `criar`. */
  expiraEm: number;
}

/**
 * Sessão contextual do evento: JWT de capacidade, só em memória.
 *
 * SEM CONSUMIDOR NESTA FATIA. A escrita v3 é servida pelo monólito
 * (`CheckInControllerV3` → `UsuarioLogadoResolver` + permissão no banco) e usa o
 * token do usuário; mandar o token contextual nela tomaria 401, porque ele sai
 * com `aud=checkin-service`. Quem vai consumir esta sessão são as rotas do
 * próprio serviço — busca, balanço e SSE em `/checkin/api/v3/**` —, e o
 * roteamento delas é fatia separada de infra (ver o plano da GUE-359 em
 * `2026-08-27-gue-359-checkin-v3-write.md`).
 *
 * Fica provido pelo componente da tela, não em `root`: sair da rota destrói o
 * provider e o token vai com ele. Nada de `localStorage`/`sessionStorage` — um
 * JWT de capacidade de evento sobrevivendo à navegação é exatamente o que a
 * GUE-359 tirou do caminho.
 */
@Injectable()
export class CheckinSessaoService {
  private readonly http = inject(HttpClient);

  private sessao: Sessao | null = null;
  private emVoo: { eventoId: string; obs: Observable<string> } | null = null;

  /**
   * Token da sessão do evento. Reusa enquanto sobra folga; renova sozinho
   * quando entra na margem.
   *
   * O `eventoId` faz parte da identidade da sessão: sessão de outro evento
   * nunca é reaproveitada, porque o token é limitado a UM evento (claim
   * `evento_id`) e o serviço rejeita a divergência. Guardar por evento evita
   * mandar a requisição só para tomar 403.
   */
  token(eventoId: string): Observable<string> {
    const atual = this.sessao;
    if (atual?.eventoId === eventoId && atual.expiraEm - Date.now() > MARGEM_RENOVACAO_MS) {
      return of(atual.token);
    }
    return this.criar(eventoId);
  }

  /** 401 no serviço: descarta a sessão para a próxima tentativa pedir outra. */
  invalidar(): void {
    this.sessao = null;
    this.emVoo = null;
  }

  private criar(eventoId: string): Observable<string> {
    // Dois scans ao mesmo tempo pedem UMA sessão, não duas.
    if (this.emVoo?.eventoId === eventoId) return this.emVoo.obs;

    // Chamada direta ao `HttpClient`: a função gerada `criarCheckinSession`
    // devolve Promise, e esta sessão depende de Observable frio para reusar,
    // renovar e coalescer pedidos simultâneos sem atravessar microtask.
    const url = `/api/v3/eventos/${eventoId}/checkin-sessions`;

    const obs = this.http.post<CheckinSessionResponse>(url, undefined).pipe(
      map((resp) => {
        // `expiresIn` (relativo) e não `expiresAt`: o relógio do tablet de
        // portaria erra com frequência, e um relógio adiantado descartaria uma
        // sessão boa a cada chamada.
        this.sessao = {
          eventoId,
          token: resp.accessToken,
          expiraEm: Date.now() + resp.expiresIn * 1000,
        };
        this.emVoo = null;
        return resp.accessToken;
      }),
      catchError((err: unknown) => {
        this.emVoo = null;
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.emVoo = { eventoId, obs };
    return obs;
  }
}

@Injectable()
export class CheckinV3Service {
  private readonly http = inject(HttpClient);

  /**
   * Registra um check-in por código, forçando ou não.
   *
   * `forcar` é campo do contrato desde a v3.8.0 (guedder-api `7c61d6630`), então
   * a tela não precisa mais cair no PUT legado para ignorar a janela: um único
   * transporte responde pelos dois casos. Só ofereça força quando o conflito
   * disse `forceAllowed: true`.
   *
   * A `Idempotency-Key` nasce UMA vez por operação do operador, fora do pipe:
   * toda repetição abaixo reenvia a mesma chave. É o que impede o check-in
   * duplicado quando o servidor gravou e a resposta se perdeu no caminho. Uma
   * nova chave só aparece quando o operador decide tentar de novo — e aí é outra
   * operação de verdade, incluindo o "forçar" depois de um 409.
   *
   * Autenticação: token do USUÁRIO, pelo `AddTokenInterceptor` de sempre. Este
   * path é servido pelo monólito (`CheckInControllerV3` → `UsuarioLogadoResolver`
   * + permissão no banco), que não aceita o JWT de capacidade — mandar o token
   * contextual aqui tomaria 401. Ver a nota em `CheckinSessaoService`.
   */
  registrar(
    eventoId: string,
    codigo: string,
    forcar = false,
  ): Observable<CheckinResultadoDto> {
    const idempotencyKey = crypto.randomUUID();

    return this.enviar(eventoId, codigo, forcar, idempotencyKey).pipe(
      retry({
        count: 2,
        delay: (err, tentativa) =>
          ehTransitorio(err) ? timer(tentativa * 400) : throwError(() => err),
      }),
    );
  }

  private enviar(
    eventoId: string,
    codigo: string,
    forcar: boolean,
    idempotencyKey: string,
  ): Observable<CheckinResultadoDto> {
    const payload: CheckinPayload = { tipo: CheckinPayloadTipo.CODIGO, valor: codigo, forcar };

    // Chamada direta ao `HttpClient`: a função gerada `registrarCheckIn` só
    // aceita `{ signal }` e não tem por onde passar a `Idempotency-Key` — que é
    // exatamente o que impede o check-in duplicado.
    return this.http.post<CheckinResultadoDto>(
      `/api/v3/eventos/${eventoId}/check-ins`,
      payload,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
  }
}
